import React, { useState, useEffect } from 'react';

const CalendarView = ({ selectedItem, selectedDate, onDateSelect }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(false);

  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ];

  const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

  const loadCalendarData = React.useCallback(async () => {
    try {
      setLoading(true);
      
      // Generate calendar data for the current month
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      const data = {};
      
      // Generate some mock data for demonstration
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dateStr = date.toISOString().split('T')[0];
        
        // Skip weekends for school days
        if (date.getDay() !== 0 && date.getDay() !== 6) {
          // Randomly assign some courses and homework
          const hasCourses = Math.random() > 0.3;
          const hasHomework = Math.random() > 0.4;
          
          if (hasCourses || hasHomework) {
            data[dateStr] = {
              courses: hasCourses ? Math.floor(Math.random() * 6) + 1 : 0,
              homework: hasHomework ? Math.floor(Math.random() * 4) + 1 : 0
            };
          }
        }
      }
      
      setCalendarData(data);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadCalendarData();
  }, [currentDate, selectedItem, loadCalendarData]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleDateClick = (date) => {
    if (date) {
      const dateStr = date.toISOString().split('T')[0];
      onDateSelect(dateStr);
    }
  };

  const getDateStatus = (date) => {
    if (!date) return null;
    
    const dateStr = date.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];
    const selected = selectedDate;
    
    if (dateStr === today) return 'today';
    if (dateStr === selected) return 'selected';
    
    const dayData = calendarData[dateStr];
    if (dayData && (dayData.courses > 0 || dayData.homework > 0)) {
      return 'has-data';
    }
    
    return 'normal';
  };

  const getDateData = (date) => {
    if (!date) return null;
    const dateStr = date.toISOString().split('T')[0];
    return calendarData[dateStr] || null;
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <h3>月曆視圖</h3>
        <p>顯示整月的課程和家課概覽</p>
      </div>

      <div className="calendar-navigation">
        <button 
          className="nav-btn"
          onClick={() => navigateMonth(-1)}
        >
          ←
        </button>
        <h4>{currentDate.getFullYear()}年 {monthNames[currentDate.getMonth()]}</h4>
        <button 
          className="nav-btn"
          onClick={() => navigateMonth(1)}
        >
          →
        </button>
      </div>

      <div className="calendar-grid">
        <div className="calendar-header-row">
          {dayNames.map(day => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}
        </div>
        
        <div className="calendar-body">
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} className="calendar-day empty"></div>;
            }

            const status = getDateStatus(date);
            const dayData = getDateData(date);
            const isSelected = selectedDate && date.toISOString().split('T')[0] === selectedDate;

            return (
              <div 
                key={index} 
                className={`calendar-day ${status} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleDateClick(date)}
              >
                <div className="day-number">{date.getDate()}</div>
                
                {dayData && (
                  <div className="day-indicators">
                    {dayData.courses > 0 && (
                      <div className="indicator courses">
                        📚 {dayData.courses}
                      </div>
                    )}
                    {dayData.homework > 0 && (
                      <div className="indicator homework">
                        📝 {dayData.homework}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="calendar-legend">
        <h4>圖例說明</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color today"></div>
            <span>今天</span>
          </div>
          <div className="legend-item">
            <div className="legend-color selected"></div>
            <span>選中日期</span>
          </div>
          <div className="legend-item">
            <span className="legend-icon">📚</span>
            <span>有課程內容的日期</span>
          </div>
          <div className="legend-item">
            <span className="legend-icon">📝</span>
            <span>有家課的日期</span>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>載入月曆資料中...</p>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
